// ─── ShareReportCard.tsx — Partage viral d'un rapport de scan ────────────────
//
// Affiché après un scan réussi, pour les visiteurs anonymes ET les comptes.
// Transforme chaque scan gratuit en lien public propageable (/r/{uuid}) :
//   - Anonyme  : POST /public/scan/{uuid}/enable-share {token}  (jeton HMAC)
//   - Compte   : PATCH /scans/history/{uuid}/share               (toggle authentifié)
// Une fois le partage activé, révèle le lien + boutons copier / LinkedIn / X.
//
import { useState } from 'react';
import { Share2, Copy, Check, Linkedin, Link2 } from 'lucide-react';
import SkuIcon from './SkuIcon';
import { apiClient } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';
import { captureReportShared } from '../lib/analytics';
import type { ShareMethod, ShareContext } from '../lib/analytics';

interface ShareReportCardProps {
  scanUuid:        string;
  domain:          string;
  score:           number;
  isAuthenticated: boolean;
  /** Jeton HMAC renvoyé par /scan — requis pour le partage anonyme. */
  shareToken?:     string | null;
}

export default function ShareReportCard({
  scanUuid, domain, score, isAuthenticated, shareToken,
}: ShareReportCardProps) {
  const { lang } = useLanguage();
  const [shared,   setShared]   = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [copied,   setCopied]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  // Un anonyme sans jeton ne peut pas partager — on masque la carte.
  if (!isAuthenticated && !shareToken) return null;

  const shareUrl = `${window.location.origin}/r/${scanUuid}`;
  const context: ShareContext = isAuthenticated ? 'results_auth' : 'results_anon';

  const shareText = lang === 'fr'
    ? `Score de cybersécurité de ${domain} : ${score}/100 — analysé avec Wezea`
    : `${domain} cybersecurity score: ${score}/100 — scanned with Wezea`;

  async function enableSharing() {
    setEnabling(true);
    setError(null);
    try {
      if (isAuthenticated) {
        const { data } = await apiClient.patch<{ public_share: boolean }>(
          `/scans/history/${scanUuid}/share`,
        );
        // Le endpoint authentifié est un toggle ; après un scan frais il est à
        // false → un appel l'active. S'il revenait à false (déjà partagé puis
        // re-basculé), on le ré-active pour garantir l'état "partagé".
        if (data.public_share) {
          setShared(true);
        } else {
          await apiClient.patch(`/scans/history/${scanUuid}/share`);
          setShared(true);
        }
      } else {
        await apiClient.post(`/public/scan/${scanUuid}/enable-share`, { token: shareToken });
        setShared(true);
      }
    } catch {
      setError(lang === 'fr'
        ? "Le partage n'a pas pu être activé. Réessayez."
        : 'Sharing could not be enabled. Please try again.');
    } finally {
      setEnabling(false);
    }
  }

  function share(method: ShareMethod) {
    captureReportShared(method, context);
    if (method === 'copy') {
      try {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* clipboard indisponible — l'input reste sélectionnable */ }
      return;
    }
    const url = method === 'linkedin'
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
      : `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="sku-card rounded-xl p-5 mb-2">
      <div className="flex items-center gap-3 mb-1">
        <SkuIcon color="#0d9488" size={32}>
          <Share2 size={14} className="text-[var(--color-accent-strong)]" />
        </SkuIcon>
        <h3 className="text-[var(--color-ink)] font-semibold text-sm">
          {lang === 'fr' ? 'Partager ce rapport' : 'Share this report'}
        </h3>
      </div>
      <p className="text-[var(--color-muted)] text-xs mb-4">
        {lang === 'fr'
          ? 'Générez un lien public de ce rapport — à montrer à votre équipe, un client ou votre réseau.'
          : 'Generate a public link to this report — to show your team, a client, or your network.'}
      </p>

      {!shared ? (
        <>
          <button
            onClick={enableSharing}
            disabled={enabling}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold sku-btn-primary disabled:opacity-60"
          >
            <Link2 size={14} />
            {enabling
              ? (lang === 'fr' ? 'Activation…' : 'Enabling…')
              : (lang === 'fr' ? 'Créer un lien de partage' : 'Create a share link')}
          </button>
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          {!isAuthenticated && (
            <p className="text-[var(--color-faint)] text-[11px] mt-2 leading-snug">
              {lang === 'fr'
                ? 'Le lien reste privé tant que vous ne l\'activez pas.'
                : 'The link stays private until you enable it.'}
            </p>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 text-xs font-mono bg-[var(--color-inset)] border border-[var(--color-border)] rounded-lg px-3 py-2 text-[var(--color-body)]"
            />
            <button
              onClick={() => share('copy')}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-border-strong)] text-[var(--color-body)] text-xs font-semibold transition"
            >
              {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
              {copied ? (lang === 'fr' ? 'Copié' : 'Copied') : (lang === 'fr' ? 'Copier' : 'Copy')}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => share('linkedin')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-border-strong)] text-[var(--color-body)] text-xs font-semibold transition"
            >
              <Linkedin size={13} /> LinkedIn
            </button>
            <button
              onClick={() => share('x')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-border-strong)] text-[var(--color-body)] text-xs font-semibold transition"
            >
              <span className="font-black text-sm leading-none">𝕏</span> {lang === 'fr' ? 'Partager' : 'Share'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
